// Copyright (c) Docugami, Inc. All rights reserved.

import { skipToken } from '@reduxjs/toolkit/dist/query';
import { aaCardSeparatorColor, cellBackgroundGray, greyText, labelGray, machineGreen, themeWhite } from 'components/shell/presentation/AppStyles';
import { useGetLiveQaModelStatsQuery } from 'store/frontend/frontendSlice';
import styled from 'styled-components';
import { dgFontFamily } from '../common/Fonts';
import { dgIconNames, useDgIcons } from 'hooks/dgIcons';
import { TooltipHost } from '@fluentui/react';
import { useMemo } from 'react';

interface ModelAccuracyBarProps {
  docsetId: string
}

const width = 135;

const MeterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const Meter = styled.div`
  width: ${width}px;
  height: 8px;
  border-radius: 3px;
  background: ${themeWhite};
  border: 1px solid ${aaCardSeparatorColor};
  display: flex;
  flex-direction: row;
  padding: 2px;
`;

const MeterLabel = styled.div`
  font-family: ${dgFontFamily};
  font-weight: 600;
  font-size: 12px;
  line-height: 100%;
  vertical-align: middle;
  color: ${labelGray};
`;

const Mercury = styled.div< { width: number, fillColor: string } >`
  height: 100%;
  width: ${props => `${props.width}px`};
  background-color: ${props => `${props.fillColor}`};
`;

const CurrentAccuracyMercury = styled(Mercury)`
  border-top-left-radius: 2px;
  border-bottom-left-radius: 2px;
`;

const PeakAccuracyMercury = styled(Mercury)`
  border-top-right-radius: 2px;
  border-bottom-right-radius: 2px;
`;

export const ModelAccuracyBar = (props: ModelAccuracyBarProps): JSX.Element => {
  const { data: stats, isLoading: queryLoading } = useGetLiveQaModelStatsQuery(props.docsetId ?? skipToken);
  const latestModelStats = !!stats?.modelStats ? stats.modelStats[stats.modelStats.length - 1] : null;
  const latestModelF1Score = latestModelStats?.exactMatchF1Score ?? 0;
  const peakModelF1Score = useMemo(() => !!stats?.modelStats ? Math.max(...stats.modelStats.map((modelStat) => modelStat.exactMatchF1Score)) : 0, [stats]);

  const dgIcons = useDgIcons();

  let confidenceTooltipContent = null;
  if (latestModelStats) {
    const confidence = latestModelStats.confidence < 0.5 ? 'low' : 'medium';
    confidenceTooltipContent = `The confidence level of this score is ${confidence}. Confidence will improve as more documents are confirmed.`;
  } else if (!queryLoading) {
    confidenceTooltipContent = 'Accuracy will be calculated once more documents have been confirmed.';
  }

  return (
    <MeterContainer>
      <MeterLabel>ACCURACY</MeterLabel>
      {!!confidenceTooltipContent && <TooltipHost content={confidenceTooltipContent}>
        {dgIcons.icon(
          dgIconNames.DgInfo,
          '',
          12,
          12,
          greyText,
          ''
        )}
      </TooltipHost>
      }
      {latestModelStats &&
        <Meter>
          <TooltipHost content={`Current Accuracy: ${latestModelF1Score.toFixed(0)}%`}>
            <CurrentAccuracyMercury width={(width - 6) * latestModelF1Score / 100} fillColor={machineGreen}/>
          </TooltipHost>
          <TooltipHost content={`Peak Accuracy: ${peakModelF1Score.toFixed(0)}%`}>
            <PeakAccuracyMercury width={(width - 6) * (peakModelF1Score - latestModelF1Score) / 100} fillColor={cellBackgroundGray} />
          </TooltipHost>
        </Meter>
      }
    </MeterContainer >
  );
}
